<template lang="pug">
.function-history-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：日時
    template(#cell(datetime)="row")
      span {{ convertDate(row.item.datetime) }}
      span （
      span(:class="getWeekClass(convertWeek(row.item.datetime))") {{ convertWeek(row.item.datetime) }}
      span ）
      span {{ convertTime(row.item.datetime) }}
    // セル：機能
    template(#cell(function)="row")
      span {{ convertFunctionType(row.item.functionType) }}
    // セル：詳細ページ
    template(#cell(link)="row")
      span.link-text(href="#" @click="gotoDetailPage(row.item)") 確認する
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { PcUrl } from "@/commons/constants/PcUrl";
import { FunctionHistoriesGetModel, FunctionHistoryFunctionType, FunctionHistoryResidentsKey } from "@/commons/services/FunctionHistories";
import { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";

/**
 * ### 部屋テーブルコンポーネント
 */
@Component({})
export default class FunctionHistoryTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<FunctionHistoryTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<FunctionHistoryTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<FunctionHistoryTableItem> {
    return this.value;
  }

  /**
   * ### 日時を「yyyy年mm月dd日」形式に変換する
   */
  public convertDate(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日");
    return date;
  }

  /**
   * ### 日時から曜日文字列に変換する
   */
  public convertWeek(datetime: number): string {
    const week: string = this.$dateUtil.getWeekName(this.$dateUtil.convertNumberToDate(datetime), "ja");
    return week;
  }

  /**
   * ### 曜日に応じたCSSクラスを取得する
   */
  public getWeekClass(week: string): string {
    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 日時を「hh：MM」形式に変換する
   */
  public convertTime(datetime: number): string {
    const time: string = this.$dateUtil.convertNumberToString(datetime, "hh：MM");
    return time;
  }

  /**
   * ### 機能種別を文字列に変換する
   */
  public convertFunctionType(functionType: FunctionHistoryFunctionType): string {
    switch (functionType) {
      case FunctionHistoryFunctionType.RESIDENTS: {
        return "利用者";
      }
      case FunctionHistoryFunctionType.FAMILIES: {
        return "家族";
      }
      case FunctionHistoryFunctionType.OTHER_USER_GROUPS: {
        return "施設関係者グループ";
      }
      case FunctionHistoryFunctionType.OTHER_USER_GROUPS_MEMBER: {
        return "施設関係者グループメンバー";
      }
      case FunctionHistoryFunctionType.WAITERS: {
        return "入所申込者";
      }
      case FunctionHistoryFunctionType.SEND_MESSAGE_GROUPS: {
        return "宛先グループ";
      }
      case FunctionHistoryFunctionType.EVENTS: {
        return "イベント";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 詳細ページへ遷移する
   */
  public gotoDetailPage(item: FunctionHistoryTableItem): void {
    switch (item.functionType) {
      case FunctionHistoryFunctionType.RESIDENTS: {
        break;
      }
      case FunctionHistoryFunctionType.FAMILIES: {
        break;
      }
      case FunctionHistoryFunctionType.OTHER_USER_GROUPS: {
        break;
      }
      case FunctionHistoryFunctionType.OTHER_USER_GROUPS_MEMBER: {
        break;
      }
      case FunctionHistoryFunctionType.WAITERS: {
        const keys: FunctionHistoryResidentsKey = item.keys as FunctionHistoryResidentsKey;
        this.$router.push({ name: PcUrl.WaiterDetail, params: { facilityid: `${item.facilityId}`, residentid: `${keys.id}`, idinsystem: `${keys.idInSystem}` } });
        break;
      }
      case FunctionHistoryFunctionType.SEND_MESSAGE_GROUPS: {
        break;
      }
      case FunctionHistoryFunctionType.EVENTS: {
        break;
      }
      default: {
        break;
      }
    }
    return;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: FunctionHistoryTableItem, filter: FilterSetting): boolean {
    return this.filteringAccounts(row, filter) && this.filteringFunctionType(row, filter) && this.filteringDate(row, filter);
  }

  /**
   * ### 操作者によるフィルタリング
   *
   * - 操作者が1つも選択されていない場合はフィルタリング対象外
   * - 操作者毎にフィルタリングする
   *   - 操作者の種別に応じてフィルタリング条件は異なる
   *   - FilterAccountType.USERの場合、行情報のユーザーIDで判断
   *   - FilterAccountType.FACILITYの場合、行情報の施設IDで判断
   */
  public filteringAccounts(row: FunctionHistoryTableItem, filter: FilterSetting): boolean {
    let existChecked: boolean = false;
    for (const account of filter.accounts) {
      if (account.checked == true) {
        existChecked = true;

        if (account.type == FilterAccountType.USER && account.userId == row.userId) {
          return true;
        }
        if (account.type == FilterAccountType.FACILITY && account.facilityId == row.userFacilityId) {
          return true;
        }
      }
    }
    if (existChecked == false) {
      return true;
    }

    return false;
  }

  /**
   * ### 機能種別によるフィルタリング
   *
   * - 機能種別が1つも選択されていない場合はフィルタリング対象外
   * - 機能種別毎にフィルタリングする
   */
  public filteringFunctionType(row: FunctionHistoryTableItem, filter: FilterSetting): boolean {
    if (filter.functionType.waiters == false) {
      return true;
    }

    if (filter.functionType.waiters == true) {
      if (row.functionType == FunctionHistoryFunctionType.WAITERS) {
        return true;
      }
    }

    return false;
  }

  /**
   * ### 日付で期間指定によるフィルタリング
   */
  public filteringDate(row: FunctionHistoryTableItem, filter: FilterSetting): boolean {
    if (filter.date.start == "" && filter.date.end == "") {
      return true;
    }

    const date: number = Number(String(row.datetime).substring(0, 8));

    if (filter.date.start != "" && filter.date.end != "") {
      const splitedStart: string[] = filter.date.start.split("-");
      const start: number = this.$dateUtil.convertDateToNumber(new Date(Number(splitedStart[0]), Number(splitedStart[1]) - 1, Number(splitedStart[2])), "yyyymmdd");
      const splitedEnd: string[] = filter.date.end.split("-");
      const end: number = this.$dateUtil.convertDateToNumber(new Date(Number(splitedEnd[0]), Number(splitedEnd[1]) - 1, Number(splitedEnd[2])), "yyyymmdd");

      if (start <= date && date <= end) {
        return true;
      }
    } else if (filter.date.start != "") {
      const splitedStart: string[] = filter.date.start.split("-");
      const start: number = this.$dateUtil.convertDateToNumber(new Date(Number(splitedStart[0]), Number(splitedStart[1]) - 1, Number(splitedStart[2])), "yyyymmdd");

      if (start <= date) {
        return true;
      }
    } else if (filter.date.end != "") {
      const splitedEnd: string[] = filter.date.end.split("-");
      const end: number = this.$dateUtil.convertDateToNumber(new Date(Number(splitedEnd[0]), Number(splitedEnd[1]) - 1, Number(splitedEnd[2])), "yyyymmdd");

      if (date <= end) {
        return true;
      }
    }

    return false;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }
}

/** テーブルの列名の情報 */
export interface FunctionHistoryTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface FunctionHistoryTableItem extends FunctionHistoriesGetModel {}

export interface FilterSetting {
  // 操作者
  accounts: Array<FilterAccount>;
  // 機能種別
  functionType: {
    // 待機者
    waiters: boolean;
  };
  // 日時
  date: {
    start: string;
    end: string;
  };
}

export interface FilterAccount {
  /** 施設ID */
  facilityId: number;
  /** ユーザーID */
  userId: string;
  /** ユーザー名 */
  name: string;
  /** 種別 ユーザーor施設 */
  type: FilterAccountType;
  /** 選択しているか否か */
  checked: boolean;
}

export enum FilterAccountType {
  USER = 1,
  FACILITY = 2
}
</script>
